import { useEffect } from 'react';
import type {
	InlineEditingPubSubEvent,
	InlineEditingPubSubPayload,
	InlineEditingSubscriber,
} from './types.tsx';

/**
 * Simple pub sub used to publish and subscribe to events on the board for inline editing
 * We can use to to notify Relay powered components of changes to the board that occur using
 * epic actions or other actions that need to be reflected in the board.
 */
class InlineEditingPubSub {
	subscribers: Partial<Record<InlineEditingPubSubEvent, InlineEditingSubscriber[]>> = {};

	subscribe = (typeName: InlineEditingPubSubEvent, subscriber: InlineEditingSubscriber) => {
		this.subscribers[typeName] = [...(this.subscribers[typeName] ?? []), subscriber];
		return () => {
			this.subscribers[typeName] =
				this.subscribers[typeName]?.filter((sub) => sub !== subscriber) ?? [];
		};
	};

	publish = (typeName: InlineEditingPubSubEvent, payload: InlineEditingPubSubPayload) => {
		this.subscribers[typeName]?.forEach((subscriber) => subscriber(typeName, payload));
	};
}

const inlineEditingPubSub = new InlineEditingPubSub();

export const publishInlineEditingEvent = inlineEditingPubSub.publish;

export const useSubscribeToInlineEditingEvents = (
	event: InlineEditingPubSubEvent | InlineEditingPubSubEvent[],
	subscriber: InlineEditingSubscriber,
) => {
	useEffect(() => {
		const eventNames = Array.isArray(event) ? event : [event];

		const unsubscribes = eventNames.map((e) => inlineEditingPubSub.subscribe(e, subscriber));
		return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
	}, [event, subscriber]);
};
